/**
 * Audio one-shots via a single Howler sprite (ARCHITECTURE.md §2.5).
 *
 * GUARDED: the sprite files (`sfx.webm`/`sfx.mp3`) may be stubs/absent (the asset
 * stream notes the mp3/webm are stubs until transcoded), so every `play()` is a
 * silent no-op if the Howl fails to load — the app still runs. We unlock the
 * AudioContext on the FIRST real lobby gesture (NOT page load) per the autoplay
 * policy + the animation-and-sound skill.
 *
 * The sprite ranges come from the asset stream's `sfx.json`; we fetch it lazily
 * and fall back to a built-in map keyed by the same names if it's absent.
 */

import { Howl } from 'howler';
import { assetPaths } from '../theme/tokens';

/** Logical sound names the app fires. */
export type SfxName =
  | 'cardDeal'
  | 'chipClink'
  | 'diceRattle'
  | 'countdownBeep'
  | 'timerTick'
  | 'winJingle';

/** Map a logical name → the sprite key in `sfx.json`. */
const SPRITE_KEY: Record<SfxName, string> = {
  cardDeal: 'card_deal',
  chipClink: 'chip_clink',
  diceRattle: 'dice_roll',
  countdownBeep: 'tick_1',
  timerTick: 'timer_tick',
  winJingle: 'win_jingle',
};

/** Fallback sprite map (matches sfx.json key names + approx ranges). */
const FALLBACK_SPRITE: Record<string, [number, number]> = {
  card_deal: [100, 200],
  card_slide: [400, 300],
  chip_clink: [800, 250],
  chip_stack: [1150, 400],
  dice_roll: [1650, 600],
  dice_throw: [2350, 350],
  win_jingle: [2800, 1200],
  tick_1: [4100, 150],
  tick_2: [4350, 150],
  tick_3: [4600, 150],
  timer_tick: [4850, 100],
};

interface SfxMeta {
  readonly sprite: Record<string, [number, number]>;
}

let howl: Howl | undefined;
let attempted = false;
let available = false;

async function loadSpriteMap(): Promise<Record<string, [number, number]>> {
  try {
    const res = await fetch(assetPaths.sfxMeta);
    if (res.ok) {
      const meta = (await res.json()) as SfxMeta;
      if (meta && typeof meta.sprite === 'object') return meta.sprite;
    }
  } catch {
    /* fall through to fallback */
  }
  return FALLBACK_SPRITE;
}

function buildHowl(sprite: Record<string, [number, number]>): void {
  try {
    howl = new Howl({
      // Load the real WAV (the webm/mp3 are still stubs). Listed alone so Howler
      // can't pick an empty stub; `format` is explicit since the URL has no query.
      src: [assetPaths.sfxWav],
      format: ['wav'],
      sprite,
      volume: 0.55, // keep it subtle, not annoying (GAME plan / Phase 4 a11y)
      preload: true,
      onload: () => {
        available = true;
      },
      onloaderror: () => {
        available = false;
      },
    });
  } catch {
    available = false;
  }
}

/**
 * Unlock + warm audio. Call from the FIRST lobby gesture (ID submit / ready /
 * community-dice click) — never on page load. Safe to call repeatedly.
 */
export function unlockAudio(): void {
  if (attempted) return;
  attempted = true;
  void loadSpriteMap().then(buildHowl);
}

/** Play a one-shot. No-op if the sprite isn't loaded (guarded). */
export function playSfx(name: SfxName): void {
  if (!howl || !available) return;
  try {
    howl.play(SPRITE_KEY[name]);
  } catch {
    /* ignore */
  }
}
